import type { i18n as I18nInstance, Resource, ResourceLanguage } from 'i18next';

import { DEFAULT_NAMESPACE, isSupportedLanguage, type SupportedLanguage } from '~/i18n';

export const I18N_RESOURCES_ELEMENT_ID = 'i18n-resources';

export interface InlinedI18nResources {
  lng: SupportedLanguage;
  resources: Resource;
}

const EMPTY_TAG_PATTERN = new RegExp(`(<script[^>]*\\bid="${I18N_RESOURCES_ELEMENT_ID}"[^>]*>)(</script>)`);

const isPlainObject = (value: unknown): value is ResourceLanguage =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const collectResourceBundles = (
  instance: I18nInstance,
  lng: string,
  namespaces: readonly string[],
): ResourceLanguage => {
  const bundles: ResourceLanguage = {};

  for (const namespace of new Set([DEFAULT_NAMESPACE, ...namespaces])) {
    const bundle = instance.getResourceBundle(lng, namespace);
    if (bundle) bundles[namespace] = bundle;
  }

  return bundles;
};

export const serializeI18nResources = (lng: SupportedLanguage, namespaces: ResourceLanguage): string =>
  JSON.stringify({ lng, resources: { [lng]: namespaces } }).replace(/</g, '\\u003c');

export const injectI18nResources = (html: string, lng: SupportedLanguage, namespaces: ResourceLanguage): string => {
  if (!Object.keys(namespaces).length) return html;

  const payload = serializeI18nResources(lng, namespaces);

  return html.replace(EMPTY_TAG_PATTERN, (_match, openingTag: string, closingTag: string) =>
    [openingTag, payload, closingTag].join(''),
  );
};

export const parseI18nResources = (raw: string | null | undefined): InlinedI18nResources | null => {
  if (!raw) return null;

  let parsed: unknown;

  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }

  if (!isPlainObject(parsed)) return null;
  if (!isSupportedLanguage(parsed.lng)) return null;
  if (!isPlainObject(parsed.resources)) return null;

  const resources: Resource = {};

  for (const [language, namespaces] of Object.entries(parsed.resources)) {
    if (isPlainObject(namespaces)) resources[language] = namespaces;
  }

  if (!Object.keys(resources).length) return null;

  return { lng: parsed.lng, resources };
};
